import React, { useEffect, useState } from 'react';
import { Loader2, Save, Eye } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { BaaDocument } from './BaaDocument';
import { SignedRecord } from './BaaSignatures';
import { baaApi, errorText, formatSignedAt } from './baaApi';

const SETTING_FIELDS = [
  ['last_updated', 'Last Updated date', 'October 21, 2026'],
  ['version', 'BAA version', '1.0'],
  ['governing_law', 'Governing Law (state)', 'New York'],
  ['venue', 'Venue / Jurisdiction (county and state)', 'Suffolk County, New York'],
  ['notice_email', 'Privacy / Security notice email', 'admin@practcom.com'],
  ['business_address', 'Business address of The OnCall Bot LLC', '123 Main St, City, ST 12345'],
];

const SettingsForm = ({ token }) => {
  const [s, setS] = useState(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { baaApi.adminSettings(token).then((r) => setS(r.settings)).catch((e) => setMsg(errorText(e))); }, [token]);
  const save = async () => {
    setBusy(true);
    try {
      const r = await baaApi.adminSaveSettings(token, s);
      setS(r.settings);
      setMsg('Saved. New signers will see these values.');
    } catch (e) { setMsg(errorText(e)); } finally { setBusy(false); }
  };
  if (!s) return <p className="text-sm text-slate-400">{msg || 'Loading…'}</p>;
  return (
    <div className="space-y-4" data-testid="baa-admin-settings">
      <div className="grid md:grid-cols-2 gap-4">
        {SETTING_FIELDS.map(([k, label, ph]) => (
          <div key={k}>
            <Label className="text-xs text-slate-600">{label}</Label>
            <Input data-testid={`baa-admin-${k}`} value={s[k] || ''} placeholder={ph} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="mt-1" />
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={busy} data-testid="baa-admin-save" className="rounded-full">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save BAA details</>}
        </Button>
        {msg && <span className="text-sm text-slate-600" data-testid="baa-admin-msg">{msg}</span>}
      </div>
      <p className="text-xs text-slate-500">Blank fields show as highlighted placeholders in the agreement. Practices that already signed keep the exact copy they signed.</p>
    </div>
  );
};

const AgreementRow = ({ a, onView }) => (
  <tr className="border-b last:border-0 text-sm">
    <td className="py-2 pr-3 font-medium text-slate-900">{a.practiceName}</td>
    <td className="py-2 pr-3">{a.representativeName}<div className="text-xs text-slate-500">{a.title} · {a.email}</div></td>
    <td className="py-2 pr-3 whitespace-nowrap">{formatSignedAt(a.signedAt)}</td>
    <td className="py-2 pr-3">{a.baaVersion}</td>
    <td className="py-2 pr-3 text-xs text-slate-500">{a.ip}</td>
    <td className="py-2"><Button size="sm" variant="outline" onClick={() => onView(a.id)} data-testid={`baa-admin-view-${a.id}`}><Eye className="w-3.5 h-3.5 mr-1" />View</Button></td>
  </tr>
);

export const BaaAdminPanel = ({ adminToken }) => {
  const [data, setData] = useState(null);
  const [view, setView] = useState(null);
  useEffect(() => { baaApi.adminAgreements(adminToken).then(setData).catch(() => setData({ agreements: [] })); }, [adminToken]);
  const openView = (id) => baaApi.adminAgreement(adminToken, id).then((r) => setView(r.agreement));

  return (
    <div className="space-y-6" data-testid="baa-admin-panel">
      <section className="rounded-2xl border bg-white p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-1">Business Associate Agreement details</h3>
        <p className="text-sm text-slate-500 mb-4">Fill in the blanks of the BAA that practices sign. The OnCall Bot LLC is the Business Associate.</p>
        <SettingsForm token={adminToken} />
      </section>
      <section className="rounded-2xl border bg-white p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-1">Signed agreements</h3>
        <p className="text-sm text-slate-500 mb-4" data-testid="baa-admin-count">
          {data ? `${data.agreements.length} signed${data.totalPractices ? ` · ${data.totalPractices} practices total` : ''}` : 'Loading…'}
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead><tr className="text-xs uppercase tracking-wide text-slate-500 border-b">
              <th className="py-2 pr-3">Practice</th><th className="py-2 pr-3">Signed by</th><th className="py-2 pr-3">Signed (ET)</th><th className="py-2 pr-3">Version</th><th className="py-2 pr-3">IP</th><th />
            </tr></thead>
            <tbody>{(data?.agreements || []).map((a) => <AgreementRow key={a.id} a={a} onView={openView} />)}</tbody>
          </table>
        </div>
      </section>
      <Dialog open={!!view} onOpenChange={(v) => !v && setView(null)}>
        <DialogContent className="max-w-5xl w-[96vw] max-h-[90vh] overflow-y-auto" data-testid="baa-admin-view-dialog">
          <DialogTitle>Signed BAA · {view?.practiceName}</DialogTitle>
          {view && <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6"><BaaDocument text={view.documentText} /><SignedRecord a={view} /></div>}
        </DialogContent>
      </Dialog>
    </div>
  );
};
